/**
 * Member availability and roster model — pure, I/O-free.
 *
 * Named crew with qualifications feed the BA-shortfall and structural-operator
 * rules instead of (or in addition to) the per-unit manual count. The roster
 * is the "integration" data source; standalone mode omits it and the rules
 * fall back to the per-unit summary.
 */

// ── Qualification codes ────────────────────────────────────────────────────────

export const QUALIFICATION_CODES = [
  'ba-operator',
  'ba-supervisor',
  'structural-ff',
  'crew-leader',
  'incident-controller',
  'hazmat-operator',
  'chainsaw',
  'rescue-operator',
] as const;

export type QualificationCode = (typeof QUALIFICATION_CODES)[number];

export const QUALIFICATION_LABELS: Record<QualificationCode, string> = {
  'ba-operator': 'BA operator',
  'ba-supervisor': 'BA supervisor',
  'structural-ff': 'Structural FF',
  'crew-leader': 'Crew leader',
  'incident-controller': 'IC-qualified',
  'hazmat-operator': 'Hazmat operator',
  chainsaw: 'Chainsaw',
  'rescue-operator': 'Rescue / extrication',
};

// ── Member ─────────────────────────────────────────────────────────────────────

export interface Member {
  id: string;
  name: string;
  callsign?: string;
  brigade?: string;
  agency: string;
  qualifications: QualificationCode[];
}

// ── Availability ───────────────────────────────────────────────────────────────

export const AVAILABILITY_STATUSES = [
  'available',
  'responding',
  'on-scene',
  'unavailable',
] as const;

export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

/** A member is "active" (deployable / deployed) when any of these. */
export const ACTIVE_STATUSES: readonly AvailabilityStatus[] = [
  'available',
  'responding',
  'on-scene',
];

export interface MemberAvailability {
  memberId: string;
  status: AvailabilityStatus;
  /** ISO-8601 timestamp when status was last confirmed. */
  confirmedAt: string;
  /** Source: manual entry or live integration feed. */
  source: 'manual' | 'integration';
  /** Appliance callsign the member is assigned to (when known). */
  assignedUnit?: string;
}

// ── Roster ─────────────────────────────────────────────────────────────────────

export interface MemberRoster {
  incidentId: string;
  members: Member[];
  availability: MemberAvailability[];
  /** ISO-8601 timestamp of last roster update (any member or availability). */
  lastUpdatedAt: string;
  /** Origin: 'manual' = all manual, 'integration' = live feed, 'synthetic' = fixture/demo. */
  source: 'manual' | 'integration' | 'synthetic';
}

// ── Roster helpers ─────────────────────────────────────────────────────────────

/** Members whose availability is in an active state (available/responding/on-scene). */
export function activeMembers(roster: MemberRoster): Member[] {
  const activeIds = new Set(
    roster.availability
      .filter((a) => ACTIVE_STATUSES.includes(a.status))
      .map((a) => a.memberId),
  );
  return roster.members.filter((m) => activeIds.has(m.id));
}

/** Total active crew from the roster. */
export function rosterCrewCount(roster: MemberRoster): number {
  return activeMembers(roster).length;
}

/** Count active BA operators from the roster — a subset of the total crew. */
export function rosterBAOperators(roster: MemberRoster): number {
  return activeMembers(roster).filter((m) => m.qualifications.includes('ba-operator')).length;
}

/** Members with a specific qualification who are active. */
export function rosterQualified(roster: MemberRoster, qual: QualificationCode): Member[] {
  return activeMembers(roster).filter((m) => m.qualifications.includes(qual));
}
